import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

/**
 * MobileMenuButton + slide-out NavDrawer.
 *
 * Renders a hamburger button (mobile-only) that opens a full-height slide-out
 * drawer containing the same NAV items as the desktop sidebar. The drawer is
 * mounted in a fixed-position layer with a dismissable backdrop.
 *
 * Usage in any layout:
 *   const [mobileNavOpen, setMobileNavOpen] = React.useState(false);
 *   <MobileMenuButton onClick={() => setMobileNavOpen(true)} />
 *   <MobileNavDrawer
 *      open={mobileNavOpen}
 *      onClose={() => setMobileNavOpen(false)}
 *      items={NAV_ITEMS}
 *      roleLabel="Client Portal"
 *      user={user}
 *      onLogout={handleLogout}
 *   />
 */
export function MobileMenuButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open menu"
      data-testid="mobile-menu-button"
      className="lg:hidden inline-flex items-center justify-center size-9 rounded-md text-slate-700 hover:bg-slate-100 transition-colors -ml-1"
    >
      <Menu className="size-5" strokeWidth={1.8} />
    </button>
  );
}

export function MobileNavDrawer({ open, onClose, items, roleLabel, user, onLogout, badges = {} }) {
  // Lock body scroll while drawer open
  React.useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Close on Escape
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="lg:hidden fixed inset-0 z-[80]" data-testid="mobile-nav-drawer">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
        data-testid="mobile-nav-backdrop"
      />
      {/* Drawer panel — slides in from left */}
      <aside
        className="absolute left-0 top-0 bottom-0 w-[82vw] max-w-[320px] bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 h-16 border-b border-slate-200">
          <Link to="/" className="flex items-center gap-2.5" onClick={onClose}>
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" />
                <circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" />
                <circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
              </svg>
            </span>
            <div className="leading-tight">
              <div className="text-[14px] font-semibold tracking-tight text-slate-900">Connexis<span className="text-primary">.</span></div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{roleLabel}</div>
            </div>
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            data-testid="mobile-nav-close"
            className="inline-flex items-center justify-center size-9 rounded-md text-slate-600 hover:bg-slate-100"
          >
            <X className="size-5" strokeWidth={1.8} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-3 rounded-md text-[15px] transition-colors min-h-[44px] ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-800 hover:bg-slate-100 active:bg-slate-200"
                }`
              }
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
            >
              <n.Icon className="size-[18px] shrink-0" strokeWidth={1.7} />
              <span className="flex-1">{n.label}</span>
              {n.badge && badges[n.badge] > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10.5px] font-semibold">
                  {badges[n.badge] > 9 ? "9+" : badges[n.badge]}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-2.5 px-2 py-2">
            <Avatar className="size-8">
              <AvatarFallback className="bg-slate-900 text-white text-xs">
                {(user?.name || "??").split(" ").map(s => s[0]).slice(0, 2).join("")}
              </AvatarFallback>
            </Avatar>
            <div className="leading-tight min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-500 truncate">{user?.company || user?.email}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { onClose(); onLogout(); }}
            className="w-full justify-start mt-1"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="size-4" /> Sign out
          </Button>
        </div>
      </aside>
    </div>
  );
}

/**
 * MobileBottomNav — fixed bottom-of-viewport tap nav with the 4-5 most-used
 * routes per role. Mobile-only. Renders inside the layout root.
 */
export function MobileBottomNav({ items, badges = {} }) {
  return (
    <>
      {/* Spacer so page content never hides behind the bottom nav */}
      <div className="lg:hidden h-16" aria-hidden />
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 flex items-stretch justify-around"
        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0px)" }}
        data-testid="mobile-bottom-nav"
      >
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            className={({ isActive }) =>
              `relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[60px] transition-colors ${
                isActive ? "text-[#0055FF]" : "text-slate-500 active:text-slate-900"
              }`
            }
            data-testid={`mobile-bnav-${n.label.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
          >
            <span className="relative">
              <n.Icon className="size-[19px]" strokeWidth={1.8} />
              {n.badge && badges[n.badge] > 0 && (
                <span className="absolute -top-1 -right-2 inline-flex items-center justify-center min-w-[14px] h-[14px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold leading-none">
                  {badges[n.badge] > 9 ? "9+" : badges[n.badge]}
                </span>
              )}
            </span>
            <span className="text-[10px] font-medium tracking-tight">{n.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}
